import { TFileId } from 'types';
import { postMediaFile } from 'backend/additionalInformation';
import React, { useReducer } from 'react';
import { FormImageUploaderActionTypes } from './formImageUploaderActions';
import {
	FormImageUploaderContext,
	formImageUploaderInitialState,
	TImageOrientation,
	TTypeOfErrorModalWindow,
	TTypeOfModalWindow,
} from './formImageUploaderContext';
import { formImageUploaderReducer } from './formImageUploaderReducer';

interface FormImageUploaderStateProps {
	setFileId: (fileId: TFileId) => void;
}

const FormImageUploaderState: React.FC<FormImageUploaderStateProps> = ({ children, setFileId }) => {
	const [state, dispatch] = useReducer(formImageUploaderReducer, formImageUploaderInitialState);

	const setImageOrientation = (value: TImageOrientation) => {
		dispatch({
			type: FormImageUploaderActionTypes.SET_IMAGE_ORIENTATION,
			payload: {
				imageOrientation: value,
			},
		});
	};

	const setLoading = (value: boolean) => {
		dispatch({
			type: FormImageUploaderActionTypes.SET_LOADING,
			payload: {
				isLoading: value,
			},
		});
	};

	const setFullSizeSrc = (value: string | null) => {
		dispatch({
			type: FormImageUploaderActionTypes.SET_FULL_SIZE_SRC,
			payload: {
				src: value,
			},
		});
	};

	const setCroppedSizeSrc = (value: string | null) => {
		dispatch({
			type: FormImageUploaderActionTypes.SET_CROPPED_SIZE_SRC,
			payload: {
				src: value,
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: FormImageUploaderActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const changeTypeOfErrorModalWindow = (value: TTypeOfErrorModalWindow) => {
		dispatch({
			type: FormImageUploaderActionTypes.CHANGE_TYPE_OF_ERROR_MODAL_WINDOW,
			payload: {
				typeOfErrorModalWindow: value,
			},
		});
	};

	const setLoadingImageError = (value: boolean) => {
		dispatch({
			type: FormImageUploaderActionTypes.SET_LOADING_IMAGE_ERROR,
			payload: {
				loadingImageError: value,
			},
		});
	};

	const uploadImage = async (image: Blob) => {
		setLoading(true);
		const formData = new FormData();
		const createImageName = () => {
			const indexOfBlobTypeSeparator = image.type.indexOf('/');
			const fileExtension = image.type.slice(indexOfBlobTypeSeparator + 1);
			return `image.${fileExtension}`;
		};
		formData.append('file', image, createImageName());
		const sourceUrl = URL.createObjectURL(image);
		try {
			// console.log(formData);
			const response = await postMediaFile({ data: formData });
			setFileId(response.data.fileId);
			setCroppedSizeSrc(sourceUrl);
		} catch (err) {
			changeTypeOfErrorModalWindow('fileUploadError');
		} finally {
			setLoading(false);
		}
	};

	return (
		<FormImageUploaderContext.Provider
			value={{
				setImageOrientation,
				setLoading,
				setFullSizeSrc,
				setCroppedSizeSrc,
				changeTypeOfModalWindow,
				changeTypeOfErrorModalWindow,
				uploadImage,
				setLoadingImageError,
				imageOrientation: state.imageOrientation,
				isLoading: state.isLoading,
				fullSizeSrc: state.fullSizeSrc,
				croppedSizeSrc: state.croppedSizeSrc,
				typeOfModalWindow: state.typeOfModalWindow,
				typeOfErrorModalWindow: state.typeOfErrorModalWindow,
				loadingImageError: state.loadingImageError,
			}}>
			{children}
		</FormImageUploaderContext.Provider>
	);
};

export default FormImageUploaderState;
